import { api } from "@/convex/_generated/api";
import type { SupportedLocale } from "@/i18n-locales";
import { localizedPath, type RouteKey } from "@/lib/routes";
import type { SiteSearchDocument } from "@/lib/site-search";
import type { ToolDefinition } from "@/features/tools/catalog";
import { usePublicServices } from "@/pages/services/_lib/use-public-services";
import { useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type StaticPageDefinition = {
  key: RouteKey;
  namespace: string;
  titleKey: string;
  descriptionKey: string;
};

const STATIC_PAGES: StaticPageDefinition[] = [
  {
    key: "home",
    namespace: "home",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "about",
    namespace: "about",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "careers",
    namespace: "careers",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "services",
    namespace: "services",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "editorial",
    namespace: "editorial",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "sponsored",
    namespace: "sponsored",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "portfolio",
    namespace: "portfolio",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "partners",
    namespace: "partners",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "blog",
    namespace: "magazine",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "shop",
    namespace: "shop",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "tools",
    namespace: "tools",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "contact",
    namespace: "contact",
    titleKey: "support_page.meta.title",
    descriptionKey: "support_page.meta.description",
  },
  {
    key: "faq",
    namespace: "contact",
    titleKey: "faq_page.meta.title",
    descriptionKey: "faq_page.meta.description",
  },
  {
    key: "inquiries",
    namespace: "contact",
    titleKey: "inquiries.meta.title",
    descriptionKey: "inquiries.meta.description",
  },
  {
    key: "privacy",
    namespace: "privacy",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "cookie",
    namespace: "cookie",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "terms",
    namespace: "terms",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
  {
    key: "withdrawal",
    namespace: "withdrawal",
    titleKey: "meta.title",
    descriptionKey: "meta.description",
  },
];

export function useSiteSearchDocuments(
  locale: SupportedLocale,
  includeDynamic = true,
) {
  const { i18n } = useTranslation();
  const [toolDefinitions, setToolDefinitions] = useState<
    readonly ToolDefinition[]
  >([]);
  const serviceT = i18n.getFixedT(locale, "services");
  const { services } = usePublicServices(locale, serviceT, {
    enabled: includeDynamic,
  });
  const articles = useQuery(
    api.magazine.listArticles,
    includeDynamic ? { source: "all", limit: 120 } : "skip",
  );
  const supportPosts = useQuery(
    api.crm.forum.listSearchableFaqs,
    includeDynamic ? { locale } : "skip",
  );

  useEffect(() => {
    if (!includeDynamic || toolDefinitions.length > 0) return;
    let active = true;
    void import("@/features/tools/catalog").then((module) => {
      if (active) setToolDefinitions(module.TOOL_DEFINITIONS);
    });
    return () => {
      active = false;
    };
  }, [includeDynamic, toolDefinitions.length]);

  return useMemo<SiteSearchDocument[]>(() => {
    const commonT = i18n.getFixedT(locale, "common");
    const pages = STATIC_PAGES.map((page) => {
      const pageT = i18n.getFixedT(locale, page.namespace);
      const title = pageT(page.titleKey);
      const description = pageT(page.descriptionKey);
      return {
        id: `page-${page.key}`,
        kind: "page" as const,
        title,
        description,
        href: localizedPath(page.key, locale),
        keywords: [
          commonT(`nav.${page.key}`),
          localizedPath(page.key, locale).split("/").at(-1) ?? "",
        ].filter(Boolean),
      };
    });

    const serviceDocuments = services.map((service) => ({
      id: `service-${service.serviceKey}`,
      kind: "service" as const,
      title: service.title,
      description: service.summary || service.description,
      href: `${localizedPath("services", locale)}/${service.slug}`,
      keywords: [
        service.tagline,
        service.description,
        service.description2,
        ...service.features,
      ].filter(Boolean),
      meta: commonT("search.kind.service"),
    }));

    const toolsT = i18n.getFixedT(locale, "tools");
    const toolDocuments = toolDefinitions
      .filter((tool) => tool.status === "published")
      .map((tool) => ({
        id: `tool-${tool.id}`,
        kind: "tool" as const,
        title: toolsT(`catalog.${tool.id}.title`),
        description: toolsT(`catalog.${tool.id}.description`),
        href: `${localizedPath("tools", locale)}/${tool.slugs[locale]}`,
        keywords: [
          toolsT(`catalog.${tool.id}.seoDescription`),
          toolsT(`categories.${tool.category}`),
          tool.slugs[locale],
        ].filter(Boolean),
        meta: commonT("search.kind.tool"),
      }));

    const articleDocuments = (articles ?? []).map((article) => ({
      id: `article-${article.source}-${article.externalId}`,
      kind: "article" as const,
      title: article.title,
      description: article.excerpt || article.contentPreview || article.title,
      href: `${localizedPath("blog", locale)}/${article.source}/${article.externalId}`,
      keywords: [
        article.category,
        article.author,
        article.source,
        article.sourceName,
      ].filter((value): value is string => Boolean(value)),
      meta: article.sourceName,
    }));

    // Only staff FAQs/knowledge-base content is searchable. Customer support
    // threads and private tickets deliberately stay outside the public index.
    const supportDocuments = (supportPosts ?? []).map((post) => ({
      id: `support-${post.id}`,
      kind: "support" as const,
      title: post.subject,
      description: post.excerpt || post.body || post.subject,
      href: localizedPath("faq", locale),
      keywords: [post.serviceKey, post.body].filter((value): value is string =>
        Boolean(value),
      ),
      meta: commonT("search.kind.support"),
    }));

    return [
      ...pages,
      ...serviceDocuments,
      ...toolDocuments,
      ...articleDocuments,
      ...supportDocuments,
    ];
  }, [articles, i18n, locale, services, supportPosts, toolDefinitions]);
}
